//numberingApi.js

const API_BASE = "/api/numbering";

const authHeaders = (json = true) => ({
  ...(json ? { "Content-Type": "application/json" } : {}),
  Authorization: "Bearer " + localStorage.getItem("token"),
});

// Read the server's error message (for example "Financial year is closed
// for this date") so the user sees the real reason.
async function parseError(res, fallback) {
  try {
    const body = await res.json();
    return body?.error || body?.message || fallback;
  } catch {
    return fallback;
  }
}

// Accept a date ('YYYY-MM-DD', ISO string or Date) and return 'YYYY-MM-DD'.
// For old callers that still send a financial year label such as '2025-26',
// use 1 April of that year so the right FY is picked.
function toDocumentDate(value) {
  if (!value) return undefined; // server falls back to today
  if (value instanceof Date) {
    const p = (n) => String(n).padStart(2, "0");
    return `${value.getFullYear()}-${p(value.getMonth() + 1)}-${p(value.getDate())}`;
  }
  const s = String(value);
  if (/^\d{4}-\d{2}$/.test(s)) return `${s.substring(0, 4)}-04-01`; // '2025-26'
  return s.substring(0, 10);
}

/* ------------------------------------------------------------------ */
/* Settings screen                                                     */
/* ------------------------------------------------------------------ */

// Active config of one document type
export async function fetchNumberingConfig(client_id, document_type) {
  const res = await fetch(
    `${API_BASE}/config?client_id=${encodeURIComponent(client_id)}&document_type=${encodeURIComponent(document_type)}`,
    { headers: authHeaders(false) }
  );
  if (!res.ok) throw new Error(await parseError(res, "Failed to fetch numbering config"));
  return res.json();
}

// Active config of every document type for the client
export async function fetchAllNumberingConfigs(client_id) {
  const res = await fetch(
    `${API_BASE}/configs?client_id=${encodeURIComponent(client_id)}`,
    { headers: authHeaders(false) }
  );
  if (!res.ok) throw new Error(await parseError(res, "Failed to fetch numbering configs"));
  const data = await res.json();
  return Array.isArray(data) ? data : data.data || [];
}

// Save policy, prefix, suffix, next number and padding
// config = { client_id, document_type, prefix, suffix, next_number, min_length, reset_policy }
export async function updateNumberingConfig(config) {
  const res = await fetch(`${API_BASE}/config`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(config),
  });
  if (!res.ok) throw new Error(await parseError(res, "Failed to update numbering config"));
  return res.json();
}

// "Reset to 1": starts a new series, old numbers are untouched
export async function resetNumberingConfig({ client_id, document_type }) {
  const res = await fetch(`${API_BASE}/config/reset`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ client_id, document_type }),
  });
  if (!res.ok) throw new Error(await parseError(res, "Failed to reset numbering"));
  return res.json();
}

/* ------------------------------------------------------------------ */
/* Document creation                                                   */
/* ------------------------------------------------------------------ */

// Next number using the client's policy and the document's OWN date
// (booking date, reporting date, invoice date, purchase date).
// Returns { document_number, fyid, series }.
// Note: this consumes a number. For real documents prefer generating the
// number on the server inside the save transaction.
export async function generateDocumentNumber(client_id, document_type, document_date) {
  const res = await fetch(`${API_BASE}/generate`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      client_id,
      document_type,
      document_date: toDocumentDate(document_date),
    }),
  });
  if (!res.ok) throw new Error(await parseError(res, `HTTP error! Status: ${res.status}`));
  return res.json();
}

export async function createDocumentRecord(data) {
  const res = await fetch(`/api/document-record`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await parseError(res, "Failed to create document record"));
  return res.json();
}

export async function createDocument(client_id, document_type, description) {
  const res = await fetch("/api/document", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ client_id, document_type, description }),
  });
  if (!res.ok) throw new Error(await parseError(res, `HTTP error! Status: ${res.status}`));
  return res.json();
}